/**
 * Payment Synchronization System
 * Ensures payment updates are reflected across all modules in real-time
 */

class PaymentSyncManager {
  constructor() {
    this.listeners = new Map();
  }

  /**
   * Subscribe to payment update events
   * @param {string} eventType - Type of event ('invoice_payment_updated', 'closed_order_payment_updated', 'payment_updated')
   * @param {Function} callback - Callback function to execute
   */
  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType).add(callback);
  }

  /**
   * Unsubscribe from payment update events
   * @param {string} eventType - Type of event
   * @param {Function} callback - Callback function to remove
   */
  off(eventType, callback) {
    if (this.listeners.has(eventType)) {
      this.listeners.get(eventType).delete(callback);
    }
  }

  /**
   * Emit payment update event to all subscribers
   * @param {string} eventType - Type of event
   * @param {Object} data - Payment update data
   */
  emit(eventType, data) {
    console.log(`🔄 PaymentSync: Emitting ${eventType}`, data);
    
    if (this.listeners.has(eventType)) {
      this.listeners.get(eventType).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in payment sync callback for ${eventType}:`, error);
        }
      });
    }

    // Also emit generic 'payment_updated' event
    if (eventType !== 'payment_updated') {
      this.emit('payment_updated', { ...data, originalEvent: eventType });
    }
  }

  /**
   * Notify all modules about invoice payment update
   * @param {Object} invoiceData - Updated invoice data
   */
  notifyInvoicePaymentUpdate(invoiceData) {
    this.emit('invoice_payment_updated', {
      type: 'invoice',
      id: invoiceData.id || invoiceData._id,
      documentNumber: invoiceData.invoiceNumber || invoiceData.invoice_number,
      totalAmount: invoiceData.totalAmount || invoiceData.total_amount,
      paidAmount: invoiceData.amountReceived || invoiceData.amount_received,
      paymentStatus: invoiceData.paymentStatus || invoiceData.payment_status,
      customerName: invoiceData.customer?.name || invoiceData.customer_name,
      updatedAt: new Date().toISOString(),
      data: invoiceData
    });
  }

  /**
   * Notify all modules about closed order payment update
   * @param {Object} orderData - Updated closed order data
   */
  notifyClosedOrderPaymentUpdate(orderData) {
    this.emit('closed_order_payment_updated', {
      type: 'closed_order',
      id: orderData.id || orderData._id,
      documentNumber: orderData.orderNumber || orderData.order_number || orderData.invoiceNumber,
      totalAmount: orderData.totalAmount || orderData.total_amount,
      paidAmount: orderData.amountReceived || orderData.amount_received,
      paymentStatus: orderData.paymentStatus || orderData.payment_status,
      customerName: orderData.customer?.name || orderData.customer_name,
      updatedAt: new Date().toISOString(),
      data: orderData
    });
  }

  /**
   * Refresh all payment-related data across modules
   */
  refreshAllPaymentData() {
    this.emit('refresh_all_payments', {
      timestamp: new Date().toISOString(),
      action: 'refresh_all'
    });
  }

  /**
   * Get all active listeners (for debugging)
   */
  getActiveListeners() {
    const result = {};
    this.listeners.forEach((callbacks, eventType) => {
      result[eventType] = callbacks.size;
    });
    return result;
  }
}

// Create singleton instance
export const paymentSync = new PaymentSyncManager();

// Export individual methods for convenience
export const {
  on: onPaymentUpdate,
  off: offPaymentUpdate,
  emit: emitPaymentUpdate,
  notifyInvoicePaymentUpdate,
  notifyClosedOrderPaymentUpdate,
  refreshAllPaymentData
} = paymentSync;

export default paymentSync;
